import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, Loader2, Truck, CheckCircle2, Circle, MapPin, CalendarClock,
  AlertCircle, ShieldCheck,
} from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, MaskedLine } from "@/components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "booking_confirmed", label: "Booking confirmed" },
  { key: "collection_scheduled", label: "Collection scheduled" },
  { key: "collected", label: "Collected" },
  { key: "in_transit", label: "In transit" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
];

const stageLabel = (key) => STAGES.find((s) => s.key === key)?.label || key;

const formatWhen = (iso) =>
  new Date(iso).toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  });

const Track = () => {
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;
    setLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/track/${encodeURIComponent(reference.trim())}`);
      setResult(res.data);
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const currentIndex = result ? STAGES.findIndex((s) => s.key === result.status) : -1;
  const eventsByStage = result
    ? Object.fromEntries(result.events.map((ev) => [ev.stage, ev]))
    : {};

  return (
    <div data-testid="track-page">
      <Seo
        title="Track Your Vehicle | Solaris Transport LTD"
        description="Follow your vehicle's journey with Solaris Transport. Enter the tracking reference from your booking confirmation to see live collection and delivery status."
        path="/track"
      />

      <section className="relative overflow-hidden border-b border-slate-200 bg-white" data-testid="track-hero">
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-700" data-testid="track-eyebrow">Vehicle tracking</p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="track-headline">
              <MaskedLine delay={0.1}>Where's my</MaskedLine>
              <MaskedLine delay={0.22}><span className="text-blue-700">vehicle?</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Enter the tracking reference from your booking confirmation to
              follow your vehicle from collection to delivery.
            </motion.p>

            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
              data-testid="track-form"
            >
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. STR-2026-0847"
                className="w-full rounded-full border border-slate-300 bg-white px-6 py-4 font-mono text-sm uppercase tracking-wider text-slate-900 placeholder-slate-400 outline-none transition-colors duration-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 sm:max-w-sm"
                data-testid="track-reference-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="track-submit-button"
                className="flex items-center justify-center gap-2 rounded-full bg-blue-700 px-8 py-4 text-base font-bold text-white transition-colors duration-200 hover:bg-blue-800 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                Track
              </button>
            </motion.form>
            <p className="mt-3 text-xs text-slate-400" data-testid="track-demo-hint">
              Demo references to try: <span className="font-mono">STR-2026-0847</span> or <span className="font-mono">STR-2026-0731</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8" data-testid="track-result-section">
        <AnimatePresence mode="wait">
          {notFound && (
            <motion.div
              key="notfound"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-6"
              data-testid="track-error"
            >
              <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
              <div>
                <h2 className="font-display text-lg font-bold text-red-900">We couldn't find that reference</h2>
                <p className="mt-1 text-sm leading-relaxed text-red-700">
                  Check the reference on your booking confirmation and try again.
                  Still stuck? Call us on <a href="tel:+447462227041" className="font-semibold underline">+44 7462 227041</a> and
                  we'll find it for you.
                </p>
              </div>
            </motion.div>
          )}

          {result && (
            <motion.div
              key={result.reference}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-900/5"
              data-testid="track-result"
            >
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 p-6 sm:p-8">
                <div>
                  <p className="font-mono text-xs font-semibold tracking-widest text-slate-400" data-testid="track-result-reference">{result.reference}</p>
                  <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="track-result-vehicle">{result.vehicle}</h2>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <MapPin className="h-4 w-4 text-blue-700" />
                    <span className="font-semibold" data-testid="track-result-collection">{result.collection}</span>
                    <span className="text-slate-300">→</span>
                    <span className="font-semibold" data-testid="track-result-delivery">{result.delivery}</span>
                  </div>
                  {result.eta && (
                    <p className="mt-2 flex items-center gap-2 text-sm text-slate-500" data-testid="track-result-eta">
                      <CalendarClock className="h-4 w-4 text-blue-700" />
                      Estimated delivery: {new Date(result.eta).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
                      result.status === "delivered"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-blue-50 text-blue-700"
                    }`}
                    data-testid="track-status-badge"
                  >
                    {result.status === "delivered" ? <CheckCircle2 className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                    {stageLabel(result.status)}
                  </span>
                  {result.demo && (
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="track-demo-badge">
                      Demo tracking data
                    </span>
                  )}
                </div>
              </div>

              <ol className="p-6 sm:p-8" data-testid="track-timeline">
                {STAGES.map((stage, i) => {
                  const ev = eventsByStage[stage.key];
                  const done = i < currentIndex;
                  const current = i === currentIndex;
                  return (
                    <li key={stage.key} className="relative flex gap-4 pb-8 last:pb-0" data-testid={`track-stage-${stage.key}`}>
                      {i < STAGES.length - 1 && (
                        <span className={`absolute left-[15px] top-8 h-full w-0.5 ${i < currentIndex ? "bg-blue-600" : "bg-slate-200"}`} />
                      )}
                      <span className="relative z-10 mt-0.5 shrink-0">
                        {done || (current && result.status === "delivered") ? (
                          <CheckCircle2 className="h-8 w-8 rounded-full bg-white text-blue-700" />
                        ) : current ? (
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-700">
                            <Truck className="h-4 w-4 text-white" />
                          </span>
                        ) : (
                          <Circle className="h-8 w-8 rounded-full bg-white text-slate-300" />
                        )}
                      </span>
                      <div className="pb-1">
                        <p className={`font-display text-base font-semibold ${current ? "text-blue-700" : done ? "text-slate-900" : "text-slate-400"}`}>
                          {stage.label}
                          {current && result.status !== "delivered" && (
                            <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">Now</span>
                          )}
                        </p>
                        {ev ? (
                          <>
                            <p className="mt-0.5 font-mono text-xs text-slate-400">{formatWhen(ev.at)}</p>
                            <p className="mt-1 text-sm text-slate-600">{ev.note}</p>
                          </>
                        ) : (
                          <p className="mt-0.5 text-sm text-slate-400">Pending</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 text-xs text-slate-500 sm:px-8">
                <ShieldCheck className="h-4 w-4 text-blue-700" />
                Your protected payment is only released when you confirm delivery.
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!result && !notFound && (
          <FadeUp className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center" data-testid="track-empty-state">
            <Truck className="mx-auto h-10 w-10 text-blue-200" />
            <p className="mt-4 text-sm text-slate-500">
              Your vehicle's journey will appear here once you enter a tracking reference.
            </p>
          </FadeUp>
        )}
      </section>
    </div>
  );
};

export default Track;
